/* eslint-disable max-len */
import React from 'react';
import { Row, Col } from 'antd';
import AboutTile from '../../AbouTile';
import { stripTags, domHtml } from '../../../utils/stripTags';

import SEO from '../../Seo';

const pageText = {
  paraOne: `Hi, I'm Muhammad Muneeb ur Rehman — a robotics engineer focused on multi-body dynamic
  systems, digital twins and reinforcement learning. My work centres on closing the gap between
  simulation and the real world through Sim2Real transfer, so that intelligent, adaptive robots
  behave the same on hardware as they do in simulation.`,
  paraTwo: `I'm a Research Assistant at the Swarm Robotics Lab (National Center of Robotics and
  Automation, UET Taxila) and a Senior Robotics Engineer at BrainSwarm Robotics, where I build
  miniature robot kits, remote robotics labs and UAV platforms. I also co-founded
  <strong>AyroX Labs</strong> and work as a Level 2 seller on Fiverr delivering robotics
  simulation projects. My interests span autonomous systems, UAVs, industrial automation and
  human-robot interaction.`,
};

const experience = [
  {
    role: 'Senior Robotics Engineer',
    org: 'BrainSwarm Robotics, Islamabad',
    period: '01/2026 - Present',
    points: [
      'Leading development of miniature robot kits and UAV platforms.',
      'Built a Flight Dynamics Model (FDM) for a quadcopter UAV in Gazebo for high-fidelity multi-rotor flight modeling.',
      'Achieved synchronized flight between a virtual twin and real quadcopter hardware, validating Sim2Real for UAV control.',
    ],
  },
  {
    role: 'Research Assistant - Swarm Robotics Lab',
    org: 'National Center of Robotics and Automation, UET Taxila',
    period: '03/2025 - Present',
    points: [
      'Indigenous robot kit development and a state-of-the-art remote robotics learning lab.',
      'Software stack for mobile robots: mapping and navigation.',
      'Digital twin of an indigenous miniature robot in a multi-physics engine integrated with ROS, used for Sim2Real validation.',
    ],
  },
  {
    role: 'Robotics Engineer',
    org: 'BrainSwarm Robotics, Islamabad',
    period: '08/2023 - 12/2025',
    points: [
      'Designed low-cost, plug-and-play, IoT-connected miniature robot kits.',
      'Deployed real robot hardware into Gazebo to validate algorithms with ROS before hardware rollout.',
      'Developed and simulated a digital twin of an indigenous miniature robot.',
    ],
  },
];

const education = [
  {
    school: 'University of Engineering and Technology, Taxila',
    degree: 'Master of Computer Engineering',
    period: '2023 - Present, CGPA 3.30 / 4.00',
    note: 'Research: Context-Aware Task Allocation for Multi-Agent Heterogeneous Robots with LLM and Vision Grounding.',
  },
  {
    school: 'Institute of Space Technology',
    degree: 'Bachelor of Mechanical Engineering',
    period: '2018 - 2022, CGPA 2.81 / 4.00',
    note: 'Final Year Project: Vision-Based Real-Time Pick and Place Robotic Arm with object localization and detection.',
  },
];

const skillGroups = [
  { label: 'Frameworks', items: ['ROS / ROS 2', 'MoveIt', 'OpenAI Gym'] },
  { label: 'Simulation', items: ['Gazebo / Ignition', 'Digital Twins', 'Sim2Real', 'UAV Flight Dynamics'] },
  { label: 'Machine Learning', items: ['Reinforcement Learning', 'Computer Vision'] },
  { label: 'Programming', items: ['Python', 'C++', 'MATLAB'] },
  { label: 'CAD', items: ['SOLIDWORKS', 'CREO Parametric'] },
  { label: 'Hardware', items: ['ESP32', 'STM32', 'AVR ATMEGA', 'OpenCR', 'Raspberry Pi', 'ARM Cortex', 'Flight Controllers'] },
];

const GH = 'https://github.com/munn33b';

const tagStyle = {
  display: 'inline-block',
  background: '#f3f5f8',
  color: '#44566C',
  borderRadius: '20px',
  padding: '4px 12px',
  margin: '0 6px 8px 0',
  fontSize: '0.8rem',
};

const AboutMe = () => {
  const description = `${pageText.paraOne} ${stripTags(pageText.paraTwo)}`;
  return (
    <>
      <div>
        <SEO
          title="About"
          description={description}
          path=""
          keywords={['Muhammad Muneeb ur Rehman', 'Robotics Engineer', 'Sim2Real', 'Digital Twin', 'ROS', 'UAV', 'Reinforcement Learning', 'Computer Vision']}
        />
        <h1 className="titleSeparate">About Me</h1>
        <p>{pageText.paraOne}</p>
        <p dangerouslySetInnerHTML={domHtml(pageText.paraTwo)} />
      </div>
      <br />

      <h1 className="titleSeparate">Projects</h1>
      <Row gutter={[20, 20]}>
        <Col xs={24} sm={24} md={12} lg={8}>
          <AboutTile
            img="meeting.png"
            alt="robotic arm"
            textH3="Vision-Based Pick & Place Arm"
            textH4="A vision-guided pick-and-place framework with object localization, detection and Sim2Real transfer."
            githubSrc={GH}
            python="80"
            ml="75"
            sim="70"
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <AboutTile
            img="motorcycle.png"
            alt="quadcopter"
            textH3="Quadcopter Sim2Real"
            textH4="Flight Dynamics Model in Gazebo with synchronized flight between a virtual twin and real UAV hardware."
            githubSrc={GH}
            sim="85"
            ros="80"
            cpp="70"
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <AboutTile
            img="web.png"
            alt="digital twin"
            textH3="Digital Twin Robot"
            textH4="Digital twin of an indigenous miniature robot in a multi-physics engine integrated with ROS."
            githubSrc={GH}
            ros="85"
            sim="80"
            python="75"
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <AboutTile
            img="graduation.png"
            alt="remote lab"
            textH3="Remote Robotics Lab"
            textH4="A state-of-the-art remote learning lab letting students run real robots and validate algorithms online."
            githubSrc={GH}
            ros="80"
            python="70"
            embedded="75"
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <AboutTile
            img="coffee.png"
            alt="robot kit"
            textH3="IoT Robot Kits"
            textH4="Low-cost, plug-and-play, IoT-connected miniature robot kits designed for teaching and research."
            githubSrc={GH}
            embedded="85"
            cad="70"
            cpp="65"
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <AboutTile
            img="location.png"
            alt="multi-agent robots"
            textH3="Multi-Agent Task Allocation"
            textH4="Context-aware task allocation for heterogeneous robot teams, grounded with LLMs and vision."
            githubSrc={GH}
            ml="80"
            python="80"
            ros="70"
          />
        </Col>
      </Row>
      <br />

      <h1 className="titleSeparate">Experience</h1>
      {experience.map((job) => (
        <div key={job.role + job.period} style={{ marginBottom: '1.8rem' }}>
          <h3 style={{ marginBottom: '2px' }}>{job.role}</h3>
          <h4 style={{ fontWeight: 400, color: '#304CFD', marginBottom: '2px' }}>{job.org}</h4>
          <p style={{ color: '#8a94a6', fontSize: '0.85rem', marginBottom: '8px' }}>{job.period}</p>
          <ul style={{ paddingLeft: '1.1rem', color: '#404040' }}>
            {job.points.map((pt) => (
              <li key={pt} style={{ marginBottom: '4px' }}>{pt}</li>
            ))}
          </ul>
        </div>
      ))}
      <br />

      <h1 className="titleSeparate">Education</h1>
      {education.map((ed) => (
        <div key={ed.school} style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '2px' }}>{ed.degree}</h3>
          <h4 style={{ fontWeight: 400, color: '#304CFD', marginBottom: '2px' }}>{ed.school}</h4>
          <p style={{ color: '#8a94a6', fontSize: '0.85rem', marginBottom: '6px' }}>{ed.period}</p>
          <p style={{ color: '#404040' }}>{ed.note}</p>
        </div>
      ))}
      <br />

      <h1 className="titleSeparate">Skills</h1>
      {skillGroups.map((group) => (
        <div key={group.label} style={{ marginBottom: '1rem' }}>
          <h4 style={{ marginBottom: '8px' }}>{group.label}</h4>
          <div>
            {group.items.map((item) => (
              <span key={item} style={tagStyle}>{item}</span>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};
export default AboutMe;
